import TextInput from 'components/Inputs/TextInput/TextInput';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import Debounce from 'utils/Debounce';
import Select from 'components/Inputs/Select/Select';
import ErrorConstants from 'utils/ErrorConstants';
import ErrorsUtils from 'utils/Errors';
import {
    Grid, Text, Box, Button
} from 'components/ui';
import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import localeUtils from 'utils/LanguageLocale';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    mediaQueries, colors, radii, space
} from 'style/config';
import FormValidator from 'utils/FormValidator';
import HelperUtils from 'utils/Helpers';
import utilityApi from 'services/api/utility';
import addressUtils from 'utils/Address';
import Markdown from 'components/Markdown/Markdown';
import FrictionlessUtils from 'utils/FrictionlessCheckout';

const { formatCanadianPostalCode } = FrictionlessUtils;
const { getCurrentCountry } = localeUtils;
const PHONE_NUMBER = 'phoneNumber';
const POSTAL_CODE = 'postalCode';
const country = getCurrentCountry();

const pickupPersonFormValidators = {
    handleValidateFirstName: firstName => {
        if (FormValidator.isEmpty(firstName)) {
            return ErrorConstants.ERROR_CODES.FIRST_NAME;
        }

        return null;
    },

    handleValidateLastName: lastName => {
        if (FormValidator.isEmpty(lastName)) {
            return ErrorConstants.ERROR_CODES.LAST_NAME;
        }

        return null;
    },

    handleValidatePhoneNumber: phoneNumber => {
        if (FormValidator.isEmpty(phoneNumber)) {
            return ErrorConstants.ERROR_CODES.PHONE_NUMBER;
        }

        if (phoneNumber.length && !FormValidator.isValidPhoneNumber(phoneNumber)) {
            return ErrorConstants.ERROR_CODES.PHONE_NUMBER_INVALID;
        }

        return null;
    },

    handleValidateStreetAddressLine1: addressLine1 => {
        if (FormValidator.isEmpty(addressLine1)) {
            return ErrorConstants.ERROR_CODES.ADDRESS1;
        }

        return null;
    },

    handleValidateStreetAddressLine2: addressLine2 => {
        if (!FormValidator.isValidLength(addressLine2, 0, FormValidator.FIELD_LENGTHS.address)) {
            return ErrorConstants.ERROR_CODES.ADDRESS1_LONG;
        }

        return null;
    },

    handleValidatePostalCode: invalidPostalCode => postalCodeValue => {
        // const { invalidPostalCode } = this.state;

        if (FormValidator.isEmpty(postalCodeValue)) {
            return ErrorConstants.ERROR_CODES.ZIP_CODE_NON_US;
        } else if (!FormValidator.isValidZipCode(postalCodeValue, country) || invalidPostalCode) {
            return ErrorConstants.ERROR_CODES.INVALID_ZIP_CODE;
        }

        return null;
    },

    handleValidateCity: cityValue => {
        if (FormValidator.isEmpty(cityValue)) {
            return ErrorConstants.ERROR_CODES.CITY;
        } else if (!FormValidator.isValidLength(cityValue, 1, FormValidator.FIELD_LENGTHS.city)) {
            return ErrorConstants.ERROR_CODES.CITY_LONG;
        }

        return null;
    }
};

function usePickupFormValidation(isGuestCheckout) {
    const firstNameInput = useRef(null);
    const lastNameInput = useRef(null);
    const emailInput = useRef(null);
    const phoneNumberInput = useRef(null);
    const addressLine1Input = useRef(null);
    const addressLine2Input = useRef(null);
    const postalCodeInput = useRef(null);
    const cityInput = useRef(null);
    const stateSelect = useRef(null);

    const validateForm = () => {
        ErrorsUtils.clearErrors();
        const fieldsForValidation = [
            firstNameInput.current,
            lastNameInput.current,
            phoneNumberInput.current,
            addressLine1Input.current,
            postalCodeInput.current,
            cityInput.current,
            stateSelect.current
        ];

        if (isGuestCheckout) {
            fieldsForValidation.push(emailInput.current);
        }

        ErrorsUtils.collectClientFieldErrors(fieldsForValidation);

        return !ErrorsUtils.validate(fieldsForValidation);
    };

    return {
        firstNameInput,
        lastNameInput,
        emailInput,
        phoneNumberInput,
        addressLine1Input,
        addressLine2Input,
        postalCodeInput,
        cityInput,
        stateSelect,
        validateForm
    };
}

const PickupPersonForm = ({
    // pickupPerson,
    isGuestCheckout,
    isCanada,
    localization,
    shippingGroupId,
    shippingAddress,
    createShippingAddress,
    getStateList,
    user,
    callback,
    isEditMode,
    createDraftHalAddress,
    order
}) => {
    const {
        firstName: firstNameLabel,
        lastName: lastNameLabel,
        email: emailLabel,
        emailRequiredText,
        phoneNumber,
        streetAddress,
        addressLine2,
        postalCode: postalCodeLabel,
        invalidPostalCode: invalidPostalCodeLabel,
        enterPostalCode,
        city: cityLabel,
        province,
        modelButtonText,
        cancelButtonText,
        idRequiredEditMode
    } = localization;

    const {
        firstNameInput,
        lastNameInput,
        emailInput,
        phoneNumberInput,
        addressLine1Input,
        addressLine2Input,
        postalCodeInput,
        cityInput,
        stateSelect,
        validateForm
    } = usePickupFormValidation(isGuestCheckout);

    const [pickupPerson, setPickupPerson] = useState({
        firstName: null,
        lastName: null,
        phoneNumber: null,
        email: null,
        addressLine1: null,
        addressLine2: null,
        city: null,
        state: null,
        postalCode: null,
        country: null
    });
    const [statesList, setStatesList] = useState([]);
    const [showCityAndState, setShowCityAndState] = useState(false);
    const [invalidPostalCode, setInvalidPostalCode] = useState(false);

    const pickupPersonSaveData = useMemo(() => {
        const halAddress = order?.halAddress;

        if (!halAddress) {
            return null;
        }

        return {
            firstName: halAddress?.firstName || '',
            lastName: halAddress?.lastName || '',
            phoneNumber: halAddress?.phone || '',
            email: halAddress?.email || '',
            pickUpPersonAddress1: halAddress?.pickUpPersonAddress1 || '',
            pickUpPersonAddress2: halAddress?.pickUpPersonAddress2 || '',
            pickUpPersonCity: halAddress?.pickUpPersonCity || '',
            pickUpPersonState: halAddress?.pickUpPersonState || '',
            postalCode: halAddress?.postalCode || '',
            pickUpPersonPostalCode: halAddress?.pickUpPersonPostalCode || ''
        };
    }, [order?.halAddress]);

    const updatePickupPerson = event => {
        const name = event.target.name;
        let value = event.target.value;

        if (name === PHONE_NUMBER) {
            value = value.replace(HelperUtils.specialCharacterRegex, '');

            // Check if the specific phone input is autofilled
            const isPhoneAutofilled = phoneNumberInput.current?.inputElementRef?.matches(':-webkit-autofill');

            if (isPhoneAutofilled) {
                // Skip immediate update and only do delayed update
                // Use requestAnimationFrame for the fastest possible update after autofill
                requestAnimationFrame(() => {
                    // Re-read the value from the input after autofill completes
                    const currentValue = phoneNumberInput.current?.inputElementRef?.value || '';
                    const rawPhone = currentValue.replace(HelperUtils.specialCharacterRegex, '');

                    setPickupPerson(prev => ({
                        ...prev,
                        [name]: rawPhone
                    }));
                });

                // Return early - don't update state immediately
                return;
            }
        }

        // Normal typing - update immediately
        setPickupPerson(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePostalCode = async event => {
        let postalCode = event.target.value;
        postalCode = addressUtils.formatZipPostalCode(postalCode.toUpperCase(), ' ', 3, 6);

        //if postalCode has valid length make getStateAndCityForZipCode call
        //if user removes part of valid postalCode, reset state and city inputs
        if (postalCode?.length === 7) {
            try {
                const zipCodeData = await utilityApi.getStateAndCityForZipCode(country, postalCode);

                setPickupPerson(prev => ({
                    ...prev,
                    city: zipCodeData.city,
                    state: zipCodeData.state
                }));
                setShowCityAndState(true);
            } catch (e) {
                setInvalidPostalCode(true);
                setPickupPerson(prev => ({
                    ...prev,
                    city: null,
                    state: null
                }));
                setShowCityAndState(false);
            }
        } else {
            setPickupPerson(prev => ({
                ...prev,
                city: null,
                state: null
            }));
            setInvalidPostalCode(false);
            setShowCityAndState(false);
        }

        setPickupPerson(prev => ({
            ...prev,
            [POSTAL_CODE]: postalCode
        }));
    };

    const getStatesList = useCallback((showCityAndStateCA = false) => {
        getStateList(getCurrentCountry(), statesListResults => {
            setStatesList(statesListResults.slice(1));
            setShowCityAndState(showCityAndStateCA);
        });
    }, []);

    useEffect(() => {
        getStateList();
        setPickupPerson({
            firstName: pickupPersonSaveData?.firstName || shippingAddress?.firstName || user?.firstName,
            lastName: pickupPersonSaveData?.lastName || shippingAddress?.lastName || user?.lastName,
            phoneNumber: pickupPersonSaveData?.phoneNumber || shippingAddress?.phone || user?.phoneNumber,
            email: pickupPersonSaveData?.email || shippingAddress?.email,
            addressLine1: pickupPersonSaveData?.pickUpPersonAddress1 || shippingAddress?.pickUpPersonAddress1,
            addressLine2: pickupPersonSaveData?.pickUpPersonAddress2 || shippingAddress?.pickUpPersonAddress2,
            city: pickupPersonSaveData?.pickUpPersonCity || shippingAddress?.pickUpPersonCity,
            state: pickupPersonSaveData?.pickUpPersonState || shippingAddress?.pickUpPersonState,
            postalCode:
                pickupPersonSaveData?.pickUpPersonPostalCode ||
                (shippingAddress?.pickUpPersonAddress1 ? shippingAddress?.pickUpPersonPostalCode : null)
        });
    }, []);

    useEffect(() => {
        if (isCanada) {
            const showCityAndStateCA = Boolean(shippingAddress?.pickUpPersonPostalCode);
            getStatesList(showCityAndStateCA);
        }
    }, [isCanada, getStatesList]);

    const saveAndContinue = event => {
        event?.preventDefault();

        createDraftHalAddress(shippingAddress, shippingGroupId, shippingAddress?.halOperatingHours ?? order.halOperatingHours, () => {
            const isFormValid = validateForm();
            const halAddressPayload = {
                shippingGroupId: shippingGroupId,
                addressType: shippingAddress?.addressType,
                address: {
                    firstName: pickupPerson?.firstName,
                    lastName: pickupPerson?.lastName,
                    phone: pickupPerson?.phoneNumber,
                    address1: shippingAddress?.address1,
                    city: shippingAddress?.city,
                    state: shippingAddress?.state,
                    postalCode: shippingAddress?.postalCode,
                    country: shippingAddress?.country,
                    altPickLocationID: shippingAddress?.altPickLocationID,
                    altPickLocationType: shippingAddress?.altPickLocationType,
                    altPickLocationPartner: shippingAddress?.altPickLocationPartner,
                    altLocationPhone: shippingAddress?.altLocationPhone,
                    halCompany: shippingAddress?.halCompany,
                    email: pickupPerson?.email
                }
            };

            if (isCanada) {
                halAddressPayload.address = {
                    ...halAddressPayload.address,
                    pickUpPersonAddress1: pickupPerson?.addressLine1,
                    pickUpPersonAddress2: pickupPerson?.addressLine2,
                    pickUpPersonCity: pickupPerson?.city,
                    pickUpPersonState: pickupPerson?.state,
                    pickUpPersonPostalCode: pickupPerson?.postalCode,
                    ...(isGuestCheckout && { postalCode: formatCanadianPostalCode(halAddressPayload.address.postalCode) })
                };
            }

            const currentHalAddress = {
                ...halAddressPayload.address,
                addressType: halAddressPayload.addressType,
                ...(!isCanada && {
                    addressId: shippingAddress?.altPickLocationID
                })
            };

            if (isGuestCheckout) {
                halAddressPayload.address.shipEmail = pickupPerson?.email;
            }

            if (isFormValid) {
                createShippingAddress(halAddressPayload, currentHalAddress, callback);
            }
        });
    };

    const closeModal = e => {
        e.preventDefault();
        callback(false, isEditMode);
    };

    return (
        <>
            <Box css={styles.requiredTextContainer}>
                <Markdown
                    fontSize='sm'
                    lineHeight='1.2'
                    content={idRequiredEditMode}
                />
            </Box>
            <Grid
                columns={1}
                gap={3}
                marginTop={4}
                width={'100%'}
            >
                <Box css={styles.oneLine}>
                    <TextInput
                        name='firstName'
                        marginBottom={null}
                        required={true}
                        label={firstNameLabel}
                        value={pickupPerson?.firstName}
                        onChange={updatePickupPerson}
                        maxLength={FormValidator.FIELD_LENGTHS.name}
                        ref={firstNameInput}
                        validateError={pickupPersonFormValidators.handleValidateFirstName}
                        hideAsterisk
                    />
                </Box>
                <Box css={styles.oneLine}>
                    <TextInput
                        name='lastName'
                        marginBottom={null}
                        required={true}
                        label={lastNameLabel}
                        value={pickupPerson?.lastName}
                        onChange={updatePickupPerson}
                        maxLength={FormValidator.FIELD_LENGTHS.name}
                        ref={lastNameInput}
                        validateError={pickupPersonFormValidators.handleValidateLastName}
                        hideAsterisk
                    />
                </Box>

                {isGuestCheckout && (
                    <InputEmail
                        marginBottom={null}
                        required={true}
                        label={emailLabel}
                        infoText={emailRequiredText}
                        name='email'
                        onChange={updatePickupPerson}
                        login={pickupPerson?.email}
                        ref={emailInput}
                        hideAsterisk
                    />
                )}
                <TextInput
                    name={PHONE_NUMBER}
                    type='tel'
                    autoComplete='tel-national'
                    autoCorrect='off'
                    required={true}
                    marginBottom={null}
                    label={phoneNumber}
                    value={
                        pickupPerson?.phoneNumber?.length > 3
                            ? FormValidator.getFormattedPhoneNumber(pickupPerson?.phoneNumber)
                            : pickupPerson?.phoneNumber
                    }
                    maxLength={FormValidator.FIELD_LENGTHS.formattedPhone}
                    onKeyDown={!Sephora.isTouch && FormValidator.inputAcceptOnlyNumbers}
                    onPaste={FormValidator.pasteAcceptOnlyNumbers}
                    onChange={updatePickupPerson}
                    ref={phoneNumberInput}
                    validateError={pickupPersonFormValidators.handleValidatePhoneNumber}
                    hideAsterisk
                />
                {isCanada && (
                    <>
                        <Box css={styles.oneLine}>
                            <TextInput
                                name='addressLine1'
                                marginBottom={null}
                                required={true}
                                label={streetAddress}
                                value={pickupPerson?.addressLine1}
                                onChange={updatePickupPerson}
                                maxLength={FormValidator.FIELD_LENGTHS.address}
                                ref={addressLine1Input}
                                validateError={pickupPersonFormValidators.handleValidateStreetAddressLine1}
                                customStyle={styles.addressInput}
                                hideAsterisk
                            />
                        </Box>
                        <Box css={styles.oneLine}>
                            <TextInput
                                name='addressLine2'
                                marginBottom={null}
                                label={addressLine2}
                                value={pickupPerson?.addressLine2}
                                onChange={updatePickupPerson}
                                maxLength={FormValidator.FIELD_LENGTHS.address}
                                ref={addressLine2Input}
                                validateError={pickupPersonFormValidators.handleValidateStreetAddressLine2}
                                customStyle={styles.addressInput}
                                hideAsterisk
                            />
                        </Box>
                        <Grid
                            columns={2}
                            css={styles.grid}
                        >
                            <TextInput
                                name={POSTAL_CODE}
                                required={true}
                                marginBottom={null}
                                label={postalCodeLabel}
                                value={pickupPerson?.postalCode}
                                onChange={handlePostalCode}
                                maxLength={FormValidator.FIELD_LENGTHS.postalCode}
                                ref={postalCodeInput}
                                invalid={invalidPostalCode}
                                message={invalidPostalCode && invalidPostalCodeLabel}
                                validateError={pickupPersonFormValidators.handleValidatePostalCode(invalidPostalCode)}
                                hideAsterisk
                            />
                            {!showCityAndState && (
                                <Text
                                    marginTop={1}
                                    fontSize='sm'
                                >
                                    {enterPostalCode}
                                </Text>
                            )}
                        </Grid>
                        {showCityAndState && (
                            <Grid columns={2}>
                                <TextInput
                                    name='city'
                                    marginBottom={null}
                                    required={true}
                                    label={cityLabel}
                                    value={pickupPerson?.city}
                                    onChange={updatePickupPerson}
                                    maxLength={FormValidator.FIELD_LENGTHS.city}
                                    ref={cityInput}
                                    validateError={pickupPersonFormValidators.handleValidateCity}
                                    hideAsterisk
                                />
                                <Select
                                    label={province}
                                    name='state'
                                    value={pickupPerson?.state}
                                    required={true}
                                    onChange={updatePickupPerson}
                                    ref={stateSelect}
                                    hideAsterisk
                                    marginBottom={0}
                                >
                                    {statesList.map(stateItem => (
                                        <option
                                            key={stateItem.name}
                                            value={stateItem.name}
                                            children={stateItem.description}
                                        />
                                    ))}
                                </Select>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
            <Grid
                columns={2}
                css={styles.grid}
                marginTop={5}
            >
                <Button
                    variant='secondary'
                    onClick={Debounce.preventDoubleClick(closeModal)}
                >
                    {cancelButtonText}
                </Button>
                <Button
                    variant='primary'
                    onClick={Debounce.preventDoubleClick(saveAndContinue)}
                >
                    {modelButtonText}
                </Button>
            </Grid>
        </>
    );
};
const styles = {
    addressInput: {
        root: {
            [mediaQueries.md]: {
                display: 'grid',
                gridColumnStart: 1,
                gridColumnEnd: 3
            }
        }
    },
    requiredTextContainer: {
        backgroundColor: colors.nearWhite,
        borderRadius: radii[2] + 'px',
        padding: `${space[2]}px ${space[3]}px`,
        marginTop: space[2] + 'px'
    },

    grid: {
        gridColumnStart: 1,
        gridColumnEnd: 2
    },
    oneLine: {
        gridColumnStart: 1,
        gridColumnEnd: 1
    }
};

export default wrapFunctionalComponent(PickupPersonForm, 'PickupPersonForm');
